// src/pages/MrpContainerDetailsPivot.jsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { normalizeDateInput, toDisplayDate } from "../utils/dataFormatters";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { filterRowsByAccess } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";

const HEADER_BASE = ["Cust Code", "Retail SKU", "Product Code", "Product Name-EN", "WHSE Code"];

export default function MrpContainerDetailsPivot() {
  const accessVersion = useAccessOverrideVersion();
  const location = useLocation();
  const navigate = useNavigate();
  const rawRows = location.state?.rows;
  const sourceRows = useMemo(() => (Array.isArray(rawRows) ? rawRows : []), [rawRows]);
  const accessibleRows = useMemo(() => {
    void accessVersion;
    return filterRowsByAccess(sourceRows, {
      columns: ["cust_code", "customer", "customer_code", "warehouse", "whse_code"],
      custKeys: ["cust_code", "customer", "customer_code"],
      whsKeys: ["whse_code", "warehouse"],
      allowBlank: true,
    });
  }, [sourceRows, accessVersion]);

  const pivotData = useMemo(() => buildPivot(accessibleRows), [accessibleRows]);

  if (!accessibleRows.length) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Container Pivot - SKU x ETA (Pieces)</h1>
            <p className="text-sm text-slate-600">Based on current filters from Container Details. No data available.</p>
          </div>
          <UserAccessSummary />
        </div>
        <button
          type="button"
          onClick={() => navigate("/mrp/container-details")}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          Back
        </button>
      </div>
    );
  }

  const { etaKeys, etaLabels, pivotRows, columnTotals, grandTotal } = pivotData;

  const onExport = () => {
    const header = [...HEADER_BASE, ...etaLabels, "Grand Total"];
    const dataRows = pivotRows.map((row) => [
      row.custCode,
      row.retailSku,
      row.productCode,
      row.productName,
      row.whseCode,
      ...row.cells,
      row.rowTotal,
    ]);
    const totalsRow = ["Grand Total", "", "", "", "", "", ...columnTotals, grandTotal];
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows, totalsRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pivot");
    XLSX.writeFile(workbook, "Container Pivot - SKU by ETA.xlsx");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Container Pivot - SKU x ETA (Pieces)</h1>
          <p className="text-sm text-slate-600">Based on current filters from Container Details</p>
        </div>
        <div className="flex flex-col items-end gap-2 text-sm text-slate-600">
          <UserAccessSummary />
          <span className="whitespace-nowrap">
            {pivotRows.length.toLocaleString()} SKU rows x {etaLabels.length.toLocaleString()} ETA buckets
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate("/mrp/container-details")}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onExport}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          disabled={!pivotRows.length}
        >
          Export
        </button>
      </div>

      <div className="overflow-auto rounded border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-50">
              {HEADER_BASE.map((label) => (
                <th key={label} className="border-b px-3 py-2 text-left font-semibold text-slate-700">
                  {label}
                </th>
              ))}
              {etaLabels.map((label, index) => (
                <th key={etaKeys[index]} className="border-b px-3 py-2 text-right font-semibold text-slate-700">
                  {label}
                </th>
              ))}
              <th className="border-b px-3 py-2 text-right font-semibold text-slate-700">Grand Total</th>
            </tr>
          </thead>
          <tbody>
            {pivotRows.length === 0 ? (
              <tr>
                <td colSpan={HEADER_BASE.length + etaLabels.length + 1} className="px-3 py-6 text-center text-slate-500">
                  No pivot data available.
                </td>
              </tr>
            ) : (
              pivotRows.map((row) => (
                <tr key={`${row.custCode}||${row.retailSku}||${row.productCode}`}>
                  <td className="border-b px-3 py-2 text-slate-700">{row.custCode}</td>
                  <td className="border-b px-3 py-2 text-slate-700">{row.retailSku}</td>
                  <td className="border-b px-3 py-2 text-slate-700">{row.productCode}</td>
                  <td className="border-b px-3 py-2 text-slate-700">{row.productName}</td>
                  <td className="border-b px-3 py-2 text-slate-700">{row.whseCode || ""}</td>
                  {row.cells.map((value, idx) => (
                    <td key={etaKeys[idx]} className="border-b px-3 py-2 text-right text-slate-700">
                      {value ? value.toLocaleString() : ""}
                    </td>
                  ))}
                  <td className="border-b px-3 py-2 text-right font-semibold text-slate-700">
                    {row.rowTotal ? row.rowTotal.toLocaleString() : ""}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {pivotRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 font-semibold text-slate-800">
                <td className="border-t px-3 py-2">Grand Total</td>
                <td className="border-t px-3 py-2" />
                <td className="border-t px-3 py-2" />
                <td className="border-t px-3 py-2" />
                <td className="border-t px-3 py-2" />
                {columnTotals.map((value, idx) => (
                  <td key={etaKeys[idx]} className="border-t px-3 py-2 text-right">
                    {value ? value.toLocaleString() : ""}
                  </td>
                ))}
                <td className="border-t px-3 py-2 text-right">{grandTotal ? grandTotal.toLocaleString() : ""}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function buildPivot(rows = []) {
  if (!rows.length) {
    return {
      etaKeys: [],
      etaLabels: [],
      pivotRows: [],
      columnTotals: [],
      grandTotal: 0,
    };
  }

  const etaMap = new Map(); // iso -> display label
  const aggregateMap = new Map(); // sku|code|name|whse -> data

  rows.forEach((row) => {
    const sku = String(row.retail_sku || "").trim();
    const productCode = String(row.product_pl_id || "").trim();
    const productName = String(row.productname || "").trim();
    const whseCode = String(row.warehouse || "").trim();
    const quantity = Number(row.invoice_pic_all ?? 0) || 0;
    if (!quantity) return;

    const etaIso =
      normalizeDateInput(row.etaRaw || row.eta || "", false) ||
      (row.etaRaw || row.eta || "");
    if (!etaIso) return;

    const etaLabel = toDisplayDate(etaIso) || String(row.eta || "");
    etaMap.set(etaIso, etaLabel);

    const custCode = String(row.cust_code || row.custCode || "").trim();
    const key = `${custCode}||${sku}||${productCode}||${productName}||${whseCode}`;
    if (!aggregateMap.has(key)) {
      aggregateMap.set(key, {
        custCode,
        retailSku: sku,
        productCode,
        productName,
        whseCode,
        totals: new Map(),
        rowTotal: 0,
      });
    }

    const target = aggregateMap.get(key);
    target.totals.set(etaIso, (target.totals.get(etaIso) || 0) + quantity);
    target.rowTotal += quantity;
  });

  const etaKeys = Array.from(etaMap.keys()).sort();
  const etaLabels = etaKeys.map((iso) => etaMap.get(iso) || iso);

  const pivotRows = Array.from(aggregateMap.values())
    .filter((row) => row.rowTotal > 0)
    .sort((a, b) => {
      const custCompare = (a.custCode || "").localeCompare(b.custCode || "");
      if (custCompare !== 0) return custCompare;
      const skuCompare = a.retailSku.localeCompare(b.retailSku);
      if (skuCompare !== 0) return skuCompare;
      const codeCompare = a.productCode.localeCompare(b.productCode);
      if (codeCompare !== 0) return codeCompare;
      const whseCompare = (a.whseCode || "").localeCompare(b.whseCode || "");
      if (whseCompare !== 0) return whseCompare;
      return a.productName.localeCompare(b.productName);
    })
    .map((row) => ({
      custCode: row.custCode,
      retailSku: row.retailSku,
      productCode: row.productCode,
      productName: row.productName,
      whseCode: row.whseCode,
      cells: etaKeys.map((iso) => row.totals.get(iso) || 0),
      rowTotal: row.rowTotal,
    }));

  const columnTotals = etaKeys.map((iso, idx) =>
    pivotRows.reduce((acc, row) => acc + (row.cells[idx] || 0), 0)
  );
  const grandTotal = columnTotals.reduce((acc, value) => acc + value, 0);

  return {
    etaKeys,
    etaLabels,
    pivotRows,
    columnTotals,
    grandTotal,
  };
}
